import { EmptyObject } from 'libs/end-to-end/src/generic';
import { SSOIdentityProviderList } from 'libs/end-to-end/src/sso';
import React, { Fragment, useMemo, useState } from 'react';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTable } from 'apps/sigma2-ui/src/components/AdminLayout';
import { AdminTableRow } from 'apps/sigma2-ui/src/components/AdminLayout';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { LinkButton } from 'apps/sigma2-ui/src/components/ui/LinkButton';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const AdminSSOPage = () => {
  const { myUser, userAuthenticatedApiCall } = useUser();
  const navigate = useNavigateSafe();

  const [identityProviders, setIdentityProviders] =
    useState<SSOIdentityProviderList | null>(null);

  const [listNeedsUpdate, setListNeedsUpdate] = useState(true);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive) => {
      if (
        !isAlive() ||
        !myUser ||
        myUser.roleAssignments !== 'superAdmin' ||
        !listNeedsUpdate
      ) {
        return;
      }

      setListNeedsUpdate(false);

      const getResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/admin/sso/identity-providers`,
        requestOptions: {
          method: 'GET',
        },
        responseSchema: SSOIdentityProviderList,
      });

      if (!isAlive()) {
        return;
      }

      if (!getResult) {
        return;
      }

      setIdentityProviders(
        getResult.resultType === 'err' ? null : getResult.value,
      );
    },
    [listNeedsUpdate, myUser, userAuthenticatedApiCall],
  );

  const onDelete = useAsyncCallback(
    async (isAlive, id: string) => {
      if (!isAlive() || !myUser) {
        return;
      }

      const deleteResult = await userAuthenticatedApiCall({
        url: `${getApiBaseUrl()}/admin/sso/identity-providers/${id}`,
        requestOptions: {
          method: 'DELETE',
        },
        responseSchema: EmptyObject,
      });

      if (!deleteResult) {
        return;
      }

      if (deleteResult.resultType === 'err') {
        return;
      }

      setListNeedsUpdate(true);
    },
    [myUser, userAuthenticatedApiCall],
  );

  const rows = useMemo(() => {
    if (!identityProviders) {
      return [];
    }

    const actualRows: AdminTableRow[] = [];

    for (const provider of identityProviders) {
      actualRows.push({
        key: provider.name,
        columns: [
          {
            columnType: 'text',
            key: 'name',
            text: provider.name,
          },
          {
            columnType: 'text',
            key: 'type',
            text: provider.type,
          },
          {
            columnType: 'button',
            key: 'edit',
            buttonText: 'Edit',
            onClick: () => {
              navigate(`/sso-main/identity-provider/${provider.id}`);
            },
          },
          {
            columnType: 'button',
            key: 'delete',
            buttonText: 'Delete',
            onClick: () => {
              onDelete(provider.name);
            },
          },
        ],
      });
    }
    return actualRows;
  }, [identityProviders, navigate, onDelete]);

  const ui = useMemo(() => {
    return (
      <>
        <p>You can manage your SSO Identity Providers from this screen.</p>

        <div className="flex flex-col items-start">
          <LinkButton
            variant={LegacyButtonVariant.PRIMARY_LIGHT}
            href="/sso-main/identity-provider/new"
            data-qa="newUserBtn"
            className="mt-4"
          >
            New Identity Provider
          </LinkButton>
        </div>

        <AdminTable
          headings={['IDP Name', 'Auth Flow', 'Edit', 'Delete']}
          rows={rows}
        />
      </>
    );
  }, [rows]);

  return (
    <AdminLayout currentPage={'AdminSSOPage'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'SSO', to: '/sso-main' },
        ]}
      />
      {ui}
    </AdminLayout>
  );
};
